import { Stack, Typography } from '@mui/material';

import { AutoSaveList, useFormReadOnly } from '../../../../../common';
import { EMPTY_ASSESSMENT_METHOD_VALUES } from '../constants';
import { useAssessmentClientAge } from '../hooks';
import { MISSING_RECOMMENDED_TOOLS_SX } from '../styles';
import { TREATMENT_PLAN_V3_SECTION_HEADING_SX } from '../../../../styles';
import {
  getAssessmentMethodDeleteMessage,
  getMissingRecommendedAssessmentToolsText,
  isAssessmentMethodReady,
  mapAssessmentMethodRowToValues,
  toAssessmentMethodFieldPayload,
} from '../utils';
import { ASSESSMENT_METHOD_FORM_SCHEMA } from '../validationSchema';
import AssessmentMethodFields from './AssessmentMethodFields';
import AssessmentMethodVisuals from './AssessmentMethodVisuals';

import type {
  ITreatmentPlanV3AssessmentMethod,
  TreatmentPlanV3AssessmentMethodPayload,
} from '../../../../../../store/treatmentPlanV3';
import type { AssessmentMethodFormValues } from '../types';
import type { AssessmentMethodsSectionProps } from './types';

export default function AssessmentMethodsSection({
  methods,
  templates,
  disabled,
  isSaving,
  initialAssessmentDate,
  onCreate,
  onUpdate,
  onDelete,
  onCreateVisual,
  onUpdateVisual,
  onUpdateVisualFile,
  onDeleteVisual,
}: AssessmentMethodsSectionProps) {
  const formReadOnly = useFormReadOnly();
  const clientAge = useAssessmentClientAge(initialAssessmentDate);
  const missingRecommendedToolsText = getMissingRecommendedAssessmentToolsText(methods, clientAge);

  return (
    <Stack gap={2}>
      <Typography variant="h5" sx={TREATMENT_PLAN_V3_SECTION_HEADING_SX}>
        Assessment methods
      </Typography>
      {missingRecommendedToolsText ? (
        <Typography sx={MISSING_RECOMMENDED_TOOLS_SX}>
          The following recommended assessments are missing: {missingRecommendedToolsText}
        </Typography>
      ) : null}

      <AutoSaveList<
        AssessmentMethodFormValues,
        ITreatmentPlanV3AssessmentMethod,
        TreatmentPlanV3AssessmentMethodPayload
      >
        addButtonLabel="Add assessment"
        autoOpenWhenEmpty
        disabled={disabled}
        emptyValues={EMPTY_ASSESSMENT_METHOD_VALUES}
        entries={methods}
        getDeleteConfirm={(row) => ({
          title: 'Confirm assessment deletion?',
          message: getAssessmentMethodDeleteMessage(row),
        })}
        getRowKey={(row) => row.id}
        getRowLabel={(row) => row.tool || 'New assessment'}
        isDraftReady={isAssessmentMethodReady}
        isSaving={isSaving}
        listErrorMessage={
          methods.some((method) => !isAssessmentMethodReady(method))
            ? 'Each assessment must have at least one complete visual'
            : undefined
        }
        mapRowToValues={mapAssessmentMethodRowToValues}
        newEntryLabel="New assessment"
        onCreate={onCreate}
        onDelete={onDelete}
        onUpdate={onUpdate}
        requireCompleteDraft
        renderFields={({ formik, disabled: rowDisabled, readOnly, saveField, row }) => (
          <Stack gap={2}>
            <AssessmentMethodFields
              disabled={rowDisabled}
              formik={formik}
              readOnly={readOnly}
              saveField={saveField}
              templates={templates}
            />
            <AssessmentMethodVisuals
              disabled={rowDisabled}
              isSaving={isSaving}
              methodId={row?.id}
              onCreateVisual={onCreateVisual}
              onDeleteVisual={onDeleteVisual}
              onUpdateVisual={onUpdateVisual}
              onUpdateVisualFile={onUpdateVisualFile}
              readOnly={readOnly || formReadOnly}
              visuals={row?.visuals ?? []}
            />
          </Stack>
        )}
        toFieldPayload={toAssessmentMethodFieldPayload}
        validationSchema={ASSESSMENT_METHOD_FORM_SCHEMA}
      />
    </Stack>
  );
}
